<script setup>
import Box from '@/mini/components/Box.vue'
import Button from '@/mini/components/Button.vue'
import Sep from '@/mini/components/Sep.vue'
import Space from '@/mini/components/Space.vue'
import { Apple, BoxIso, DollarCircle, ListSelect, BrightStar, ArrowUp, ArrowDown, ThumbsUp, Trash, ArrowUpRight, ArrowDownRight, Bell } from '@iconoir/vue'
import { ref, computed, watch } from 'vue'
import { useMessage } from '@/mini/composables/useMessage'

import { useAuthStore } from '@/mini/stores/auth'
import { BACKEND_ORIGIN } from '@/config/api'

const { showSuccessMessage, showWarningMessage, showDangerMessage } = useMessage()
const authStore = useAuthStore()

const props = defineProps({
  card: { type: Object, required: true },
  expanded: { type: Boolean, default: false },
})

const emit = defineEmits(['toggle', 'remove', 'edit', 'view', 'increment', 'increment-sell', 'remove-sell', 'fetch-card', 'fetch-cardtrader', 'refetch-cardtrader', 'sell-on-cardtrader', 'check-cardtrader', 'update-cardtrader-listing', 'delete-cardtrader', 'toggle-favourite', 'toggle-to-follow', 'save-ct-price', 'save-real-price', 'reset-lowest-price'])

const newSellPrice = ref(props.card.cardtrader_sell_price ?? '')
watch(() => props.card.cardtrader_sell_price, val => { newSellPrice.value = val ?? '' })

const newSellQuantity = ref(props.card.cardtrader_sell_quantity ?? '')
watch(() => props.card.cardtrader_sell_quantity, val => { newSellQuantity.value = val ?? '' })

const storageKey = `optcg_mult_${props.card.id}`
const activeMultiplier = ref(parseFloat(localStorage.getItem(storageKey)) || null)

watch(() => props.card._multApply, signal => {
  if (signal?.mult != null) activeMultiplier.value = signal.mult
})

function setMultiplier(mult) {
  const base = props.card.real_price ? Number(props.card.real_price) : props.card.market_price
  newSellPrice.value = Math.round(base * mult * 100) / 100
  activeMultiplier.value = mult
  localStorage.setItem(storageKey, mult)
}

const lowestCtPrice = ref(null)
const isLoadingCtPrice = ref(false)
const prevCtPrice = ref(props.card.cardtrader_prev_market_price ?? null)
const prevCtPriceFetchedAt = ref(props.card.cardtrader_prev_market_price_fetched_at ?? null)
const lowestStoredCtPrice = ref(props.card.cardtrader_lowest_market_price ?? null)
const lowestStoredCtPriceFetchedAt = ref(props.card.cardtrader_lowest_market_price_fetched_at ?? null)

const ctPriceTrend = computed(() => {
  const cur = lowestCtPrice.value != null && lowestCtPrice.value > 0
    ? lowestCtPrice.value
    : (props.card.cardtrader_market_price != null ? Number(props.card.cardtrader_market_price) : null)
  const prev = prevCtPrice.value != null ? Number(prevCtPrice.value) : null
  if (!cur || !prev || cur === prev) return null
  return cur > prev ? 'up' : 'down'
})
watch(() => props.card.cardtrader_prev_market_price, val => {
  prevCtPrice.value = val ?? null
})
watch(() => props.card.cardtrader_prev_market_price_fetched_at, val => {
  prevCtPriceFetchedAt.value = val ?? null
})
watch(() => props.card.cardtrader_lowest_market_price, val => {
  lowestStoredCtPrice.value = val ?? null
})
watch(() => props.card.cardtrader_lowest_market_price_fetched_at, val => {
  lowestStoredCtPriceFetchedAt.value = val ?? null
})

function formatDateTime(value) {
  if (!value) return ''
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return ''
  return parsed.toLocaleString()
}

async function fetchLowestCtPrice() {
  if (!props.card.cardtrader_blueprint || isLoadingCtPrice.value) return
  const prefix = props.card.card_name ? `${props.card.card_name}: ` : ''
  isLoadingCtPrice.value = true
  lowestCtPrice.value = null
  try {
    const res = await authStore.authFetch(`${BACKEND_ORIGIN}/api/v1/ct/marketplace/?blueprint_id=${props.card.cardtrader_blueprint}`)
    if (!res.ok) throw new Error(`${res.status}`)
    const data = await res.json()
    const products = Object.values(data).flat()
    const filtered = products.filter(p =>
      p.properties_hash?.condition === 'Near Mint' &&
      p.properties_hash?.onepiece_language === 'jp' &&
      p.user?.can_sell_sealed_with_ct_zero === true
    )
    if (filtered.length) {
      const price = Math.min(...filtered.map(p => p.price_cents)) / 100
      lowestCtPrice.value = price
      emit('save-ct-price', props.card, price)
      showSuccessMessage(`${prefix}CT price: ${price.toFixed(2)}€`)
    } else {
      lowestCtPrice.value = -1
      showWarningMessage(`${prefix}No 🇯🇵/NM listings found`)
    }
  } catch {
    lowestCtPrice.value = null
    showDangerMessage(`${prefix}CT price fetch failed`)
  } finally {
    isLoadingCtPrice.value = false
  }
}

function rarityClass(rarity) {
  switch (rarity) {
    case 'C': return 'false-white-bg grey-text'
    case 'UC': return 'light-grey-bg dark-grey-text'
    case 'R': return 'rare-bg'
    case 'SR': return 'super-rare-bg'
    case 'SEC': return 'sec-bg'
    case 'PR': return 'promo-bg'
    case 'L': return 'leader-bg'
    default: return ''
  }
}

const CARD_COLOR_META = {
  Red: { className: 'red-bg', background: '#e05e5e', text: '#ffffff' },
  Green: { className: 'green-bg', background: '#69b687', text: '#ffffff' },
  Blue: { className: 'blue-card-bg', background: '#3e84bc', text: '#ffffff' },
  Purple: { className: 'purple-bg', background: '#ae5ab9', text: '#ffffff' },
  Black: { className: 'black-card-bg', background: '#58585a', text: '#ffffff' },
  Yellow: { className: 'yellow-bg', background: '#f3dc61', text: '#141428' },
}

function parseCardColors(value) {
  return String(value || '')
    .split(/\s*[/,&+|-]\s*|\s+/)
    .map(part => part.trim())
    .filter(Boolean)
    .filter((part, index, arr) => arr.indexOf(part) === index)
    .filter(part => CARD_COLOR_META[part])
}

function cardColorClass(value) {
  const colors = parseCardColors(value)
  if (colors.length !== 1) return ''
  return CARD_COLOR_META[colors[0]].className
}

function cardColorStyle(value) {
  const colors = parseCardColors(value)
  if (colors.length <= 1) return null

  const step = 100 / (colors.length - 1)
  const gradientStops = colors
    .map((colorName, index) => `${CARD_COLOR_META[colorName].background} ${Math.round(index * step)}%`)
    .join(', ')

  const shouldUseWhiteText = colors.some(colorName => CARD_COLOR_META[colorName].text === '#ffffff')

  return {
    background: `linear-gradient(90deg, ${gradientStops})`,
    color: shouldUseWhiteText ? '#ffffff' : '#141428',
  }
}

function confirmRemoveRealPrice() {
  if (confirm('Remove real price?')) emit('save-real-price', props.card, null)
}
</script>

<template>
  <Box size="33" :class="['box-shadow b-rad-10', card.to_buy ? 'border-2 warning-border fw-bg' : '']">
    <div v-if="!card.to_buy" class="absolute p-05 cursor-pointer z-1 b-rad-5 square" style="top:calc( var(--padding) * 0.75 ); right: calc( var(--padding) * 0.75 );" :style="{ backgroundColor: card.favourite ? 'gold' : 'transparent' }" @click="emit('toggle-favourite', card)">
      <BrightStar width="18" height="18" style="vertical-align: text-bottom;" :class="card.favourite ? 'white-text' : 'light-grey-text'"/>
    </div>
    <div v-else class="absolute p-05 cursor-pointer z-1 b-rad-5 square" style="top:calc( var(--padding) * 0.75 ); right: calc( var(--padding) * 0.75 );" :style="{ backgroundColor: card.to_follow ? '#0ea5e9' : 'transparent' }" @click="emit('toggle-to-follow', card)">
      <Bell width="18" height="18" style="vertical-align: text-bottom;" :class="card.to_follow ? 'white-text' : 'light-grey-text'"/>
    </div>
    <p v-if="card.to_buy" class="inline-block m-0 warning-bg b-rad-5 py-0 px-1 mb-1">
      <ListSelect width="16" height="16" style="vertical-align: text-bottom;" class="white-text"/>&nbsp;
      <span class="S white-text bold">Wishlist</span>
    </p>
    <p class="m-0 bold flex align-items-start px-05">
      <span v-if="card.quantity>0" class="light" style="width:20px;"><span class="bad-text">{{ card.quantity }}</span>x</span>
      <span class="cursor-pointer" @click="emit('view', card)">{{ card.card_name }}</span>
    </p>
    <p v-if="card.sell_to_sell" :class="[card.cardtrader_product_id ? 'blue-bg' : 'success-bg', 'inline-block', 'mt-05', 'mb-0', 'flex', 'relative', 'white-text', 'b-rad-5', 'light', 'align-items-start', 'px-1', 's']"
      style="left:calc(var(--margin) * -1 - 3px); border-top-left-radius: 2px; border-bottom-left-radius: 2px; box-shadow: 3px 3px rgb(0 0 0 / 15%);">
      <span v-if="card.cardtrader_product_id" class="blue-bg b-rad-5">On sale:<span v-if="card.cardtrader_sell_quantity">&nbsp;{{ card.cardtrader_sell_quantity }}</span>&nbsp;/</span><span v-else class="">To sell:&nbsp;</span><span class="">&nbsp;{{ card.sell_quantity_to_sell }}</span>
    </p>
    <div class="flex align-items-start">
      <div v-if="card.card_image" class="ps-05 pe-1 cursor-pointer" @click="emit('view', card)">
        <img :src="card.card_image" :alt="card.card_name" class="card-image py-1 img" style="width: 120px; height: auto;"/>
      </div>
      <div class="py-1 px-05">
        <p v-if="card.set_id" class="m-0 S"><span class="label">Set:</span> {{ card.set_id }}</p>
        <p v-if="card.starter_deck" class="m-0 S"><span class="label">Starter Deck:</span> <span class="XS color-box b-rad-3 px-05">{{ card.starter_deck }}</span></p>
        <p class="m-0 S"><span class="label">Number:</span> {{ card.card_number }}</p>
        <p v-if="expanded" class="m-0 S"><span class="label">Card type:</span> 
          {{ card.card_type }}&nbsp;
          <span
            class="XS flag py-0 px-05 b-rad-3"
            :class="cardColorClass(card.card_color)"
            :style="cardColorStyle(card.card_color)"
          >
            {{ card.card_color }}
          </span>
        </p>
        <p class="my-1 S">
          <span v-if="card.rarity" class="XS p-05" :class="rarityClass(card.rarity)">{{ card.rarity }}</span>
          <span v-if="card.foil === true || card.foil === 'TRUE'" class="XS p-05" :class="{ 'gold-bg': card.foil === true || card.foil === 'TRUE' }">Foil</span>
          <template v-for="entry in (card.group_entries ?? [])" :key="entry.id">
            <span v-if="entry.group_name" class="XS py-05 px-1 fw-bg">{{ entry.group_name }}</span>
          </template>
        </p>
        <Space v-if="expanded && card.group_entries && !card.to_buy"/>
        <p v-if="expanded && card.group_entries && !card.to_buy" v-for="entry in card.group_entries" :key="entry.id" class="mt-0 mb-05 lh-1">
          <BoxIso width="14" height="14" style="vertical-align: middle;" class="me-05"/>
          <template v-if="entry.quantity"><span class="false-white-box XS inline-block center m-0 px-05" style="height: 22px; padding-left: 0.5rem!important; padding-right: 0.5rem!important;"><span class="bad-text">{{ entry.quantity }}</span>x</span></template>
          <template v-if="entry.warehouse"><span class="blue-box XS inline-block center m-0" style="height: 22px; padding-left: 0.5rem!important; padding-right: 0.5rem!important;"><b>CT</b> Warehouse</span></template>
          <template v-else>
            <template v-if="entry.storage_folder"><span class="grey-box XS inline-block center m-0" style="height: 22px; padding-left: 0.5rem!important; padding-right: 0.5rem!important;"><b>{{ entry.storage_folder }}</b><template v-if="entry.storage_page"><span class="light-grey-text">/</span><b>{{ entry.storage_page }}</b></template ></span></template>
            <template v-else><span class="light-grey-box XS inline-block center m-0" style="height: 22px; padding-left: 0.5rem!important; padding-right: 0.5rem!important;"><i>free card</i></span></template>
          </template>
          <span class="black-box XS inline-block center m-0" style="height: 22px; padding-left: 0.5rem!important; padding-right: 0.5rem!important;">{{ entry.group_name }}</span>
        </p>
        <Space v-if="(card.inventory_price && card.market_price ) || card.real_price"/>
        <p v-if="expanded && card.real_price" class="m-0 S"><span class="label">Real value:</span></p>
        <p v-if="card.real_price" class="m-0 lh-1">
          <span class="bold">{{ Number(card.real_price).toFixed(2) }}€</span>
          <Trash class="grey-text bad-text-hover pointer ml-05" width="14" height="14" @click="confirmRemoveRealPrice"/>
        </p>
        <p v-if="expanded && card.inventory_price && card.market_price" class="m-0 S"><span class="label danger-text">Value<span class="light-grey-text">/Price</span>:</span></p>
        <p v-if="card.inventory_price && card.market_price" class="m-0 lh-1">
          <span class="XS light-grey-text" style="margin-right: 1px;">m</span>
          <s v-if="card.real_price" class="bad-text S">{{ Number(card.market_price).toFixed(2) }}€</s>
          <span v-else class="grey-text">{{ Number(card.market_price).toFixed(2) }}€</span>
          <span v-if="true" class="light-grey-text S">/</span>
          <span class="XS light-grey-text" style="margin-right: 1px;">i</span>
          <s v-if="card.real_price" class="bad-text S">{{ Number(card.inventory_price).toFixed(2) }}€</s>
          <span v-else>{{ Number(card.inventory_price).toFixed(2) }}€</span>
        </p>
        <p v-if="card.purchase_price" class="m-0 XS">
          <span class="label success-text">Purchased:</span>
          <b class="">{{ card.purchase_price.toFixed(2) }}€</b>
          <ThumbsUp v-if="card.purchase_price<=lowestStoredCtPrice" class="blue-text" height="16px" style="vertical-align: text-bottom;"></ThumbsUp>
        </p>
        <p v-if="lowestCtPrice === -1" class="label italic m-0 XS">No 🇯🇵/<span class="success-text">NM</span> listings found</p>
        <p v-if="card.cardtrader_market_price" class="m-0 S">
          <span class="label blue-text">CT ( 🇯🇵/<span class="success-text">NM</span>):</span>&nbsp;
          <ArrowUpRight v-if="ctPriceTrend === 'up'" :style="{'color': card.to_buy ? 'var(--danger)' : 'var(--success)', 'vertical-align': 'middle'}" :width="12" :height="12" class="mx-05"/>
          <ArrowDownRight v-else-if="ctPriceTrend === 'down'" :style="{'color': card.to_buy ? 'var(--success)' : 'var(--danger)', 'vertical-align': 'middle'}" :width="12" :height="12" class="mx-05"/>
          <b class="b">{{ card.cardtrader_market_price.toFixed(2) }}€</b>
          <span v-if="lowestStoredCtPrice>=card.cardtrader_market_price" class="flag bad-bg border bad-border b-rad-3 ms-05" style="font-size: calc(var(--p) * 0.6)!important;"><a :href="`https://www.cardtrader.com/en/cards/${card.cardtrader_blueprint}`" target="_blank" class="white-text">lowest</a></span>
          <br/><span class="XS light-grey-text block mb-05" style="line-height: 0.8!important;">{{ formatDateTime(card.cardtrader_market_price_fetched_at) }}</span>
        </p>          
        <p v-if="prevCtPrice" class="m-0 XS">
          <span class="label blue-text">
            prev:&nbsp;
          </span>
          <span>
            {{ prevCtPrice.toFixed(2) }}€
          </span>
          <span v-if="prevCtPriceFetchedAt" class="light-grey-text" style="font-size: calc(var(--p) * 0.6)!important;"> ({{ formatDateTime(prevCtPriceFetchedAt) }})</span>
        </p>
        <p v-if="lowestStoredCtPrice" class="m-0 XS">
          <span class="label blue-text">
            low:&nbsp;
          </span>
          <span>
            {{ lowestStoredCtPrice.toFixed(2) }}€
          </span>
          <span v-if="lowestStoredCtPriceFetchedAt" class="light-grey-text" style="font-size: calc(var(--p) * 0.6)!important;"> ({{ formatDateTime(lowestStoredCtPriceFetchedAt) }})</span>
        </p>
      </div>
    </div>
    <div v-if="expanded" class="p-1 border bad-border b-rad-5 mt-1 wh-bg">
      <p class="S italic bad-text">OPTCG API</p>
      <p class="m-0">
        <Button size="XS" color="bad" class="px-1 round" invert @click="emit('fetch-card', card)">Fetch card Value/Price</Button>
      </p>
    </div>
    <div>
      <div v-if="expanded" class=" w-100 border blue-border p-1 b-rad-5 mt-1 wh-bg">
        <p class="S blue-text m-0 bold italic">CardTrader
          <span v-if="card.cardtrader_product_id" class="success-bg px-05 white-text flag b-rad-5 XS">On sale</span>
        </p>
        <p v-if="expanded" class="m-0 XS">
          <span v-if="card.cardtrader_expansion"class="label">Exp:</span> <b class="blue-text me-1">{{ card.cardtrader_expansion }}</b>
          <span v-if="card.cardtrader_blueprint"class="label">BP:</span> <b class="blue-text me-1">{{ card.cardtrader_blueprint }}</b>
        </p>
        <p v-else-if="expanded && card.cardtrader_blueprint && card.cardtrader_market_price" class="m-0 XS">
          <span class="label">Lowest 🇯🇵/<span class="success-text">NM</span>:</span> <b class="blue-text">{{ Number(card.cardtrader_market_price).toFixed(2) }}€</b>
        </p>
        <Space v-if="expanded"/>
        <p v-if="expanded && card.cardtrader_blueprint" class="m-0 XS">
          <Button :link="`https://www.cardtrader.com/en/cards/${card.cardtrader_blueprint}`" target="_blank" rel="noopener noreferrer" color="blue" size="XS" class="round px-1">View ↗</Button>
          <Button v-if="expanded && card.cardtrader_blueprint" size="XS" color="blue" invert class="px-1 round" :disabled="isLoadingCtPrice" @click="fetchLowestCtPrice">{{ isLoadingCtPrice ? '...' : 'Fetch CT price' }}</Button>
          <Button v-if="lowestStoredCtPrice != null" size="XS" color="danger" invert class="px-1 round" @click="emit('reset-lowest-price', card)">Del LP ({{ lowestStoredCtPrice.toFixed(2) }}€)</Button>
          <Button
            v-if="expanded && card.cardtrader_blueprint && (lowestCtPrice > 0 || card.cardtrader_market_price)"
            size="XS" color="color" invert class="px-1 round"
            @click="emit('save-real-price', card, lowestCtPrice > 0 ? lowestCtPrice : Number(card.cardtrader_market_price))"
          >Set RP</Button>
        </p>
      </div>
      <p class="mt-1 right">
        <Button size="XS" color="blue" invert class="round" :link="`https://www.cardtrader.com/en/cards/${card.cardtrader_blueprint}`" target="_blank">CT ↗</Button>
        <Button size="XS" color="light-grey" class="round" invert @click="emit('view', card)">View</Button>
        <Button size="XS" color="light-grey" class="round" invert @click="emit('toggle', card.id)">{{ expanded ? 'Hide' : 'Details' }}</Button>
        <Button size="XS" color="warning" class="round" invert @click="emit('edit', card)">Edit</Button>
      </p>
    </div>
  </Box>
</template>

<style scoped lang="scss">
.red-bg {
    background: #e05e5e;
    color: #ffffff;
}
.green-bg {
    background: #69b687;
    color: #ffffff;
}
.blue-card-bg {
    background: #3e84bc;
    color: #ffffff;
}
.purple-bg {
    background: #ae5ab9;
    color: #ffffff;
}
.black-card-bg {
    background: #58585a;
    color: #ffffff;
}
.yellow-bg {
    background: #f3dc61;
    color: #141428;
}
.gold-bg {
    background: #ffec19;
    background: linear-gradient(45deg,rgba(255, 236, 25, 1) 0%, rgba(207, 181, 59, 1) 100%);
    /*border: 1px solid #CFB53B;*/
    color: #ffffff;
    font-weight: bold;
}
.rare-bg {
  background: #ffffff;
  background: linear-gradient(45deg,rgba(255, 255, 255, 1) 0%, rgba(179, 179, 179, 1) 100%);
  /*border: 1px solid var(--light-grey);*/
}
.leader-bg {
  background: rgb(196 64 137);
  background: linear-gradient(45deg, rgb(103 24 247) 0%, rgb(196 64 137) 100%);
  /*border: 1px solid var(--false-black);*/
  color: var(--white);
}
.super-rare-bg {
  background: #ffcb47;
  background: linear-gradient(45deg,rgba(255, 203, 71, 1) 0%, rgba(255, 125, 125, 1) 100%);
  /*border: 1px solid #FF7D7D;*/
  color: #FFFFFF;
  font-weight: bold;
}
.promo-bg {
  background: #61c2ff;
  background: linear-gradient(45deg,rgba(97, 194, 255, 1) 0%, rgba(137, 104, 227, 1) 100%);
  /*border: 1px solid #8968E3;*/
  color: #FFFFFF;
  font-weight: bold;
}
.sec-bg {
  background: #ff9900;
  background: linear-gradient(45deg,rgba(255, 153, 0, 1) 1%, rgba(209, 25, 25, 1) 100%);
  /*border: 1px solid #D11919;*/
  color: var(--white);
}
</style>
